import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldOff, ShieldCheck, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const POLL_MS = 3000;
const SHOW_MS = 6000;   // matches the tickerCard animation length in index.css
const RELEASE_MS = 1000;
const MAX_VISIBLE = 3;
const FETCH_LIMIT = 30;

const CATEGORY = { abuse: "Abusive", spam: "Spam", critical: "Hostile", rival_criticism: "Rival" };

const REMOVED = ["auto_hidden", "hidden", "deleted"];

function describe(c) {
  if (REMOVED.includes(c.status)) {
    let why = CATEGORY[c.ai?.category] || "Removed";
    if (c.matched_rule && c.matched_rule.startsWith("always_remove:")) why = `Learned rule: ${c.matched_rule.split(":")[1]}`;
    else if (c.decided_by && c.decided_by !== "system") why = "Hidden by staff";
    return { label: `Removed · ${why}`, Icon: ShieldOff, tone: "text-destructive", tab: "removed" };
  }
  if (c.status === "needs_review") return { label: "Needs review", Icon: Eye, tone: "text-amber-500", tab: "review" };
  return { label: "Kept", Icon: ShieldCheck, tone: "text-emerald-500", tab: "passed" };
}

// Live ticker, bottom right: every incoming comment appears with what COMO did, then fades up and away.
// Mounted once in App, so it keeps its place while you move between pages.
// The first fetch only records what already exists, so opening the app never floods the corner.
export default function CommentTicker() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const active = !!user && user.role !== "super_admin";
  const hidden = pathname === "/feed" || pathname === "/live";  // those pages already are the feed
  const [shown, setShown] = useState([]);
  const seen = useRef(null);
  const queue = useRef([]);
  const visible = useRef(0);

  useEffect(() => {
    if (!active) return undefined;
    seen.current = null;
    queue.current = [];
    let alive = true;
    const poll = () => {
      if (document.hidden) return;
      api.get("/comments", { params: { limit: FETCH_LIMIT } }).then(({ data }) => {
        if (!alive) return;
        if (seen.current === null) {
          seen.current = new Set(data.map((c) => c.id));
          return;
        }
        const fresh = data.filter((c) => !seen.current.has(c.id)).reverse();
        fresh.forEach((c) => seen.current.add(c.id));
        queue.current.push(...fresh);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [active, user?.client_id]);

  // Release queued items one at a time so a burst reads as a stream, not a wall.
  useEffect(() => {
    const t = setInterval(() => {
      if (visible.current >= MAX_VISIBLE || !queue.current.length) return;
      const next = queue.current.shift();
      visible.current += 1;
      setShown((cur) => [...cur, next]);
      setTimeout(() => {
        visible.current -= 1;
        setShown((cur) => cur.filter((x) => x.id !== next.id));
      }, SHOW_MS);
    }, RELEASE_MS);
    return () => clearInterval(t);
  }, []);

  if (!active || hidden) return null;

  return (
    <div
      data-testid="comment-ticker"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {shown.map((c) => {
        const { label, Icon, tone, tab } = describe(c);
        return (
          <Link
            key={c.id}
            to={`/feed?tab=${tab}&id=${c.id}`}
            data-testid="ticker-card"
            title="Show this comment in the live feed"
            className="ticker-card pointer-events-auto flex items-start gap-2.5 rounded-lg border border-border bg-card/95 px-3 py-2.5 shadow-lg backdrop-blur"
          >
            <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone}`} strokeWidth={1.75} />
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-muted-foreground">
                {label} · {c.author_name}
              </p>
              <p className="line-clamp-2 text-[13px] leading-snug">{c.text}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
